/**
 * MobileCollabsListModal
 *
 * Mobile full-screen MUI Dialog replacement for the desktop CollabsListModal.
 * Displays team collaborators as card-based list with date info, search,
 * active-only toggle, and collab detail access.
 * Slide-up transition, sticky AppBar.
 */
import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Slide,
  Fade,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import { useDispatch, useSelector } from "react-redux";
import { getTeamCollabs, setTeamsCollabs } from "../../../components/clients/clientsSlice";
import { editCollabMission, ctGetCollabMissions, countShiftsAfterMissionEnd } from "../../../components/teams/teamsSlice";
import { setCollabModalData } from "../../../components/collabs/collabsSlice";
import Langs, { Localize } from "../../../app/lang/langs";
import Button from "@mui/material/Button";
import MobileAddTeamsMembersModal from "./MobileAddTeamsMembersModal";
import moment from "moment";
import { showSuccessToast } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Transition                                                         */
/* ------------------------------------------------------------------ */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Style constants                                                    */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const cardSx = {
  mx: 1.5,
  mb: 1,
  borderRadius: "18px",
  backgroundColor: "rgba(255,255,255,0.88)",
  backdropFilter: "blur(20px) saturate(1.3)",
  WebkitBackdropFilter: "blur(20px) saturate(1.3)",
  border: "1px solid rgba(255,144,21,0.06)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.03), 0 0 0 0.5px rgba(255,255,255,0.5) inset",
  transition: TRANSITION_SMOOTH,
  "&:active": {
    transform: "scale(0.98)",
  },
};

/* ------------------------------------------------------------------ */
/*  Helper: format date                                                */
/* ------------------------------------------------------------------ */
function formatDate(value) {
  if (!value) return null;
  const d = moment(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : null;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * @param {Object}   props
 * @param {boolean}  props.visible     - controls dialog open state
 * @param {Function} props.setVisible  - toggle visibility
 * @param {string|number} props.idTeam - team id for fetching collabs
 * @param {string}   props.teamName    - team name to display in header
 */
export default function MobileCollabsListModal({ visible, setVisible, idTeam, teamName }) {
  const dispatch = useDispatch();
  const collabsList = useSelector((state) => state.clients.teamsCollabs);
  const allCollabs = useSelector((state) => state.collabs.allCollabsList);

  const [collabsListIds, setCollabsListIds] = useState([]);
  const [addModalVisibility, setAddModalVisibility] = useState(false);
  // The end date awaiting confirmation: { collab, date, mission, count }.
  const [pendingEnd, setPendingEnd] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [editingDateForCollabId, setEditingDateForCollabId] = useState(null);

  // Fetch collabs when opened
  useEffect(() => {
    if (visible && idTeam) {
      dispatch(getTeamCollabs({ id_team: idTeam }));
    } else {
      dispatch(setTeamsCollabs([]));
    }
  }, [visible, idTeam, dispatch]);

  useEffect(() => {
    setCollabsListIds(collabsList.map((collab) => collab.id_collab));
  }, [collabsList]);

  // ---- Date change handlers ----
  // Setting a member's end date ends their mission in this team, which erases their shifts here
  // after that date — the dialog shows how many before anything is saved.
  const handleDateChange = async (rowData, date) => {
    if (!rowData || !date) return;
    const missionsAction = await dispatch(ctGetCollabMissions({ id_collab: rowData.id_collab }));
    const ongoing = missionsAction.payload?.responseData?.ongoingMissions || [];
    // The member's mission in this team; the first ongoing one is what was edited before.
    const mission = ongoing.find((m) => String(m.id_team) === String(idTeam)) || ongoing[0];
    if (!mission) return;

    const countAction = await dispatch(countShiftsAfterMissionEnd({
      id_mission: mission.id_mission,
      end_date: moment(date).format("YYYY-MM-DD HH:mm:ss"),
    }));
    const countData = countAction.payload?.responseData;
    setPendingEnd({ collab: rowData, date, mission, count: countData?.status ? countData.count : "?" });
  };

  const confirmDateChange = async () => {
    if (!pendingEnd) return;
    const { collab, date, mission } = pendingEnd;
    setPendingEnd(null);
    const actionResponse = await dispatch(
      editCollabMission({
        id_collab: collab.id_collab,
        start_date: moment(collab.start_date).format("YYYY-MM-DD HH:mm:ss"),
        end_date: moment(date).format("YYYY-MM-DD HH:mm:ss"),
        id_mission: mission.id_mission,
      })
    );
    if (actionResponse.payload?.responseData?.status) {
      showSuccessToast(actionResponse.payload.responseData.message);
      dispatch(setTeamsCollabs(collabsList.map((c) =>
        c.id_collab === collab.id_collab ? { ...c, end_date: date } : c)));
    }
  };

  const denyDateChange = () => {
    setPendingEnd(null);
  };

  // ---- View collab details ----
  const viewCollabModal = (rowData) => {
    let collab = allCollabs.find((c) => c.id === rowData.id_collab);
    if (collab) {
      const modalData = { ...collab, show: true };
      dispatch(setCollabModalData(modalData));
    }
  };

  // ---- Filter: active only ----
  const collabsFilter = (collab) => {
    let now = moment();
    if (
      showActiveOnly &&
      ((collab.start_date && now.isBefore(moment(collab.start_date))) ||
        (collab.end_date && now.isAfter(moment(collab.end_date))))
    ) {
      return false;
    }
    return true;
  };

  // ---- Filter + search ----
  const filteredCollabs = useMemo(() => {
    let result = collabsList.filter(collabsFilter);
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      result = result.filter(
        (c) =>
          (c.prenom && c.prenom.toLowerCase().includes(q)) ||
          (c.nom && c.nom.toLowerCase().includes(q)) ||
          (c.email && c.email.toLowerCase().includes(q))
      );
    }
    return result;
  }, [collabsList, showActiveOnly, searchText]);

  // ---- Close handler ----
  const handleClose = () => {
    if (!visible) return;
    setVisible(false);
    setShowActiveOnly(true);
    setSearchText("");
    setEditingDateForCollabId(null);
  };

  // ---- Date chip helpers ----
  const getStartDateColor = (startDate) => {
    if (!startDate) return "default";
    return moment(startDate).isAfter(moment()) ? "success" : "default";
  };

  const getEndDateColor = (endDate) => {
    if (!endDate) return "default";
    return moment(endDate).isBefore(moment()) ? "error" : "default";
  };

  // ---- Render collab card ----
  const renderCollabCard = (collab, index) => {
    const fullName = `${collab.prenom || ""} ${collab.nom || ""}`.trim();
    const startFormatted = formatDate(collab.start_date);
    const endFormatted = formatDate(collab.end_date);
    const isEditingDate = editingDateForCollabId === collab.id_collab;

    return (
      <Fade in timeout={150 + index * 40} key={collab.id_collab || index}>
        <Card sx={cardSx} variant="outlined">
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            {/* Name row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <PersonOutlineIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.45)" }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "rgba(0,0,0,0.87)",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fullName || "—"}
              </Typography>
              <IconButton
                size="small"
                onClick={() => viewCollabModal(collab)}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  borderRadius: "12px",
                  color: "#FF9015",
                  transition: TRANSITION_SMOOTH,
                  "&:active": { transform: "scale(0.92)" },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Email */}
            {collab.email && (
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  color: "rgba(0,0,0,0.5)",
                  ml: 3.25,
                  mb: 0.75,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {collab.email}
              </Typography>
            )}

            {/* Dates row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                ml: 3.25,
              }}
            >
              {startFormatted && (
                <Chip
                  icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={startFormatted}
                  size="small"
                  color={getStartDateColor(collab.start_date)}
                  variant="outlined"
                  sx={{
                    fontSize: "0.75rem",
                    height: 26,
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                />
              )}
              {endFormatted ? (
                <Chip
                  icon={<EventBusyOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={endFormatted}
                  size="small"
                  color={getEndDateColor(collab.end_date)}
                  variant="outlined"
                  sx={{
                    fontSize: "0.75rem",
                    height: 26,
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                />
              ) : (
                <Chip
                  icon={<EditCalendarIcon sx={{ fontSize: 14 }} />}
                  label={Localize("END_DATE")}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setEditingDateForCollabId(collab.id_collab);
                  }}
                  sx={{
                    fontSize: "0.75rem",
                    height: 26,
                    borderRadius: "8px",
                    fontWeight: 600,
                    borderStyle: "dashed",
                    color: "#FF9015",
                    borderColor: "#FF9015",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(255,144,21,0.06)" },
                  }}
                />
              )}
            </Box>

            {/* Inline date input - shown when editing */}
            {isEditingDate && !collab.end_date && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: 3.25,
                  mt: 1,
                  p: 1,
                  borderRadius: "12px",
                  bgcolor: "rgba(255,144,21,0.04)",
                  border: "1px solid rgba(255,144,21,0.15)",
                }}
              >
                <input
                  type="date"
                  autoFocus
                  onChange={(e) => {
                    if (e.target.value) {
                      setEditingDateForCollabId(null);
                      handleDateChange(collab, new Date(e.target.value));
                    }
                  }}
                  style={{
                    flex: 1,
                    fontSize: "16px",
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: "1px solid rgba(255,144,21,0.3)",
                    outline: "none",
                    background: "rgba(255,255,255,0.8)",
                    color: "#333",
                    minHeight: "44px",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setEditingDateForCollabId(null)}
                  sx={{
                    minWidth: 36,
                    minHeight: 36,
                    borderRadius: "10px",
                    color: "rgba(0,0,0,0.4)",
                    "&:active": { transform: "scale(0.9)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    );
  };

  return (
    <>
      <Dialog
        fullScreen
        open={!!visible}
        onClose={handleClose}
        TransitionComponent={SlideUp}
        sx={{
          zIndex: 1400,
          "& .MuiDialog-paper": {
            background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
          },
        }}
      >
        {/* Sticky AppBar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(24px) saturate(1.5)",
            WebkitBackdropFilter: "blur(24px) saturate(1.5)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            color: "rgba(0,0,0,0.87)",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
            },
          }}
        >
          <Toolbar sx={{ minHeight: 56, px: 1 }}>
            <IconButton
              edge="start"
              onClick={handleClose}
              sx={{
                minWidth: 44,
                minHeight: 44,
                borderRadius: "12px",
                color: "rgba(0,0,0,0.7)",
                transition: TRANSITION_SMOOTH,
                "&:active": { transform: "scale(0.94)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1, ml: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  letterSpacing: "-0.01em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {teamName || <Langs str="COLLABS" />}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.5)" }}>
                {filteredCollabs.length} <Langs str="COLLABS" />
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FF9015",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FF9015",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.6)" }}>
                  {showActiveOnly ? Localize("Active collabs") : Localize("All collabs")}
                </Typography>
              }
              labelPlacement="start"
              sx={{ mr: 0 }}
            />
          </Toolbar>

          {/* Search bar */}
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={Localize("SEARCH") + "..."}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.35)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  backdropFilter: "blur(8px)",
                  fontSize: "16px",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
                  transition: TRANSITION_SMOOTH,
                  "& fieldset": { borderColor: "rgba(255,144,21,0.08)" },
                  "&.Mui-focused fieldset": {
                    borderColor: "rgba(255,144,21,0.4)",
                    boxShadow: "0 0 0 3px rgba(255,144,21,0.08)",
                  },
                },
                "& input": { fontSize: "16px", py: 1 },
              }}
            />
          </Box>
        </AppBar>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pb: 3,
            pt: 1,
            mt: 0,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {filteredCollabs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <Typography sx={{ color: "rgba(0,0,0,0.4)", fontSize: "0.95rem" }}>
                Aucun collaborateur trouvé.
              </Typography>
            </Box>
          ) : (
            filteredCollabs.map((collab, i) => renderCollabCard(collab, i))
          )}
        </Box>
      </Dialog>

      <MobileAddTeamsMembersModal
        visible={addModalVisibility}
        setVisible={setAddModalVisibility}
        prevCollabs={collabsListIds}
        idTeam={idTeam}
      />

      <Dialog
        open={!!pendingEnd}
        onClose={denyDateChange}
        sx={{
          zIndex: 1500,
          "& .MuiDialog-paper": {
            borderRadius: "18px",
            mx: 2,
            p: 2.5,
            bgcolor: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(24px)",
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 1 }}>
          {Localize("MISSION_END_SHIFTS_TITLE")}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", color: "rgba(0,0,0,0.6)", mb: 2.5 }}>
          {pendingEnd && Localize("MISSION_END_SHIFTS_MSG")
            .replace("%DATE%", moment(pendingEnd.date).format("DD/MM/YYYY"))
            .replace("%COUNT%", pendingEnd.count)}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          <Button
            onClick={denyDateChange}
            variant="outlined"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
              borderColor: "rgba(0,0,0,0.15)",
              color: "rgba(0,0,0,0.6)",
            }}
          >
            {Localize("Cancel")}
          </Button>
          <Button
            onClick={confirmDateChange}
            variant="contained"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              minHeight: 44,
              background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
              boxShadow: "0 4px 12px rgba(255,144,21,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)" },
            }}
          >
            {Localize("CONFIRM")}
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
